import { Component } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

/**
 * Catches uncaught render errors so a single broken coach-side page can't
 * unmount the whole CoachLayout tree (sidebar included).
 */
export default class CoachErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        // Keep noise low in prod logs; show stack in dev.
        // eslint-disable-next-line no-console
        console.error("[CoachErrorBoundary]", error, info?.componentStack);
    }
    reset = () => {
        this.setState({ error: null });
    };
    render() {
        if (!this.state.error) return this.props.children;
        return (
            <div data-testid="coach-error-boundary" className="max-w-2xl mx-auto px-6 md:px-10 py-16">
                <div className="border border-amber-700/60 bg-amber-950/30 p-6">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-amber-300 mb-1">Page error</div>
                            <div className="font-display text-2xl tracking-wider text-white">SOMETHING BROKE.</div>
                            <p className="mt-2 text-sm text-zinc-200">
                                This page failed to render. The rest of the console is still working — pick another tab in the sidebar, or try again.
                            </p>
                            <pre className="mt-3 text-[11px] text-amber-200/80 bg-black/40 border border-amber-900/40 px-3 py-2 overflow-x-auto max-h-32">
                                {String(this.state.error?.message || this.state.error)}
                            </pre>
                            <button
                                type="button"
                                onClick={this.reset}
                                data-testid="coach-error-retry"
                                className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase px-3 py-2 bg-brand-red text-white font-semibold"
                            >
                                <RotateCcw className="w-3.5 h-3.5" /> Try again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
